import {
  PermissionAction,
  PermissionChange,
  PermissionCheckState,
  PermissionCount,
  PermissionModuleNode,
  PermissionPreset,
  PermissionScreenNode,
  PermissionTreeNode,
} from './permission-tree.models';

/** Funções puras sobre a seleção (conjunto de claims marcadas) e a árvore. */

export function countSelected(values: string[], selected: ReadonlySet<string>): PermissionCount {
  return { selected: values.filter(value => selected.has(value)).length, total: values.length };
}

export function checkState(values: string[], selected: ReadonlySet<string>): PermissionCheckState {
  const { selected: marked, total } = countSelected(values, selected);
  if (!marked) return 'off';
  return marked === total ? 'on' : 'partial';
}

/** Marca (ou desmarca) todas as claims de `values`, sem mexer no resto da seleção. */
export function setValues(
  selected: ReadonlySet<string>,
  values: string[],
  checked: boolean
): Set<string> {
  const next = new Set(selected);
  values.forEach(value => (checked ? next.add(value) : next.delete(value)));
  return next;
}

/** Clique no quadrado de um nível: tudo marcado desmarca; parcial ou vazio marca tudo. */
export function toggleNode(selected: ReadonlySet<string>, node: PermissionTreeNode): Set<string> {
  return setValues(selected, node.values, checkState(node.values, selected) !== 'on');
}

export function screensOf(node: PermissionTreeNode): PermissionScreenNode[] {
  if (node.kind === 'screen') return [node];
  if (node.kind === 'area') return node.screens;
  return node.children.flatMap(child => (child.kind === 'area' ? child.screens : [child]));
}

/**
 * Atalhos por tela, área ou módulo:
 * - `read-only`: aparece no menu e toda a leitura ligadas, nenhuma gravação;
 * - `all`: tudo ligado;
 * - `none`: tudo desligado.
 */
export function applyPreset(
  selected: ReadonlySet<string>,
  node: PermissionTreeNode,
  preset: PermissionPreset
): Set<string> {
  if (preset === 'all') return setValues(selected, node.values, true);
  if (preset === 'none') return setValues(selected, node.values, false);

  const screens = screensOf(node);
  const readValues = screens.flatMap(screen => [
    ...(screen.menuAction ? [screen.menuAction.value] : []),
    ...screen.readActions.map(action => action.value),
  ]);
  const writeValues = screens.flatMap(screen => screen.writeActions.map(action => action.value));
  return setValues(setValues(selected, readValues, true), writeValues, false);
}

/** "toda a leitura, nenhuma gravação" e afins, para o subtítulo da tela. */
export function describeScreenSelection(
  screen: PermissionScreenNode,
  selected: ReadonlySet<string>
): string {
  const read = countSelected(
    screen.readActions.map(action => action.value),
    selected
  );
  const write = countSelected(
    screen.writeActions.map(action => action.value),
    selected
  );
  const parts: string[] = [];
  if (read.total) {
    parts.push(
      read.selected === read.total
        ? 'toda a leitura'
        : read.selected
          ? `${read.selected} de ${read.total} de leitura`
          : 'nenhuma leitura'
    );
  }
  if (write.total) {
    parts.push(
      write.selected === write.total
        ? 'toda a gravação'
        : write.selected
          ? `${write.selected} de ${write.total} de gravação`
          : 'nenhuma gravação'
    );
  }
  return parts.join(', ');
}

export interface IndexedAction {
  action: PermissionAction;
  screen: PermissionScreenNode;
}

/** Índice valor → ação e tela onde ela mora (cada claim mora numa tela só). */
export function indexActions(modules: PermissionModuleNode[]): Map<string, IndexedAction> {
  const index = new Map<string, IndexedAction>();
  modules
    .flatMap(module => screensOf(module))
    .forEach(screen =>
      [
        ...(screen.menuAction ? [screen.menuAction] : []),
        ...screen.readActions,
        ...screen.writeActions,
      ].forEach(action => index.set(action.value, { action, screen }))
    );
  return index;
}

export function screenPathLabel(screen: PermissionScreenNode): string {
  return [...screen.path, screen.label].join(' › ');
}

/** O que muda ao salvar, comparando a seleção atual com a que veio do grupo (ou vazia, no cadastro). */
export function diffSelection(
  initial: ReadonlySet<string>,
  current: ReadonlySet<string>,
  index: Map<string, IndexedAction>
): PermissionChange[] {
  const describe = (value: string, type: PermissionChange['type']): PermissionChange | null => {
    const entry = index.get(value);
    if (!entry) return null;
    const label = entry.action.kind === 'menu' ? 'Aparece no menu' : entry.action.label;
    return {
      value,
      label,
      where: screenPathLabel(entry.screen),
      type,
      kind: entry.action.kind,
      screenId: entry.screen.id,
    };
  };

  const added = [...current]
    .filter(value => !initial.has(value))
    .map(value => describe(value, 'added'));
  const removed = [...initial]
    .filter(value => !current.has(value))
    .map(value => describe(value, 'removed'));

  return [...added, ...removed].filter((change): change is PermissionChange => !!change);
}

/**
 * Cópia da árvore só com as telas que `pickValues` aceita (devolve as claims que ficam), refazendo as contagens de
 * área e módulo. Usada pela busca e pelo filtro "Só marcadas".
 */
export function filterTree(
  modules: PermissionModuleNode[],
  pickValues: (screen: PermissionScreenNode) => string[] | null
): PermissionModuleNode[] {
  const pickScreen = (screen: PermissionScreenNode): PermissionScreenNode | null => {
    const values = pickValues(screen);
    return values && values.length ? { ...screen, values } : null;
  };

  return modules
    .map(module => {
      const children = module.children
        .map(child => {
          if (child.kind === 'screen') return pickScreen(child);
          const screens = child.screens
            .map(pickScreen)
            .filter((item): item is PermissionScreenNode => !!item);
          return screens.length
            ? { ...child, screens, values: screens.flatMap(item => item.values) }
            : null;
        })
        .filter((child): child is NonNullable<typeof child> => !!child);
      return { ...module, children, values: children.flatMap(child => child.values) };
    })
    .filter(module => module.children.length > 0);
}

function normalize(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/** Alguma claim do nó está diferente do que veio salvo (bolinha âmbar na árvore). */
export function hasPendingChange(
  values: string[],
  selected: ReadonlySet<string>,
  initial: ReadonlySet<string>
): boolean {
  return values.some(value => selected.has(value) !== initial.has(value));
}

/**
 * "Só leitura" de fato: menu e toda a leitura ligados, nenhuma gravação, e há gravação para desligar. Usado no selo
 * ao lado da contagem do painel.
 */
export function isReadOnlySelection(
  node: PermissionTreeNode,
  selected: ReadonlySet<string>
): boolean {
  const screens = screensOf(node);
  const readValues = screens.flatMap(screen => [
    ...(screen.menuAction ? [screen.menuAction.value] : []),
    ...screen.readActions.map(action => action.value),
  ]);
  const writeValues = screens.flatMap(screen => screen.writeActions.map(action => action.value));
  return (
    readValues.length > 0 &&
    writeValues.length > 0 &&
    readValues.every(value => selected.has(value)) &&
    writeValues.every(value => !selected.has(value))
  );
}

/** Pedaço de texto para destacar o termo da busca (sem `innerHTML`). */
export interface TextSegment {
  text: string;
  match: boolean;
}

/** Quebra `text` nos trechos que casam com `query`, ignorando acento e caixa, como a busca. */
export function splitHighlight(text: string, query: string): TextSegment[] {
  const source = text ?? '';
  const term = normalize(query ?? '');
  if (!term || !source) return [{ text: source, match: false }];

  // Normaliza caractere a caractere e guarda de onde veio cada posição, para cortar o texto original.
  let normalized = '';
  const origin: number[] = [];
  for (let i = 0; i < source.length; i++) {
    const piece = source[i].normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
    for (let k = 0; k < piece.length; k++) origin.push(i);
    normalized += piece;
  }

  const segments: TextSegment[] = [];
  let cursor = 0;
  let from = normalized.indexOf(term);
  while (from >= 0) {
    const start = origin[from];
    const end = origin[from + term.length - 1] + 1;
    if (start > cursor) segments.push({ text: source.slice(cursor, start), match: false });
    segments.push({ text: source.slice(start, end), match: true });
    cursor = end;
    from = normalized.indexOf(term, from + term.length);
  }
  if (cursor < source.length) segments.push({ text: source.slice(cursor), match: false });
  return segments;
}

export interface SearchResultScreen {
  screen: PermissionScreenNode;
  actions: PermissionAction[];
}

/**
 * Busca por rótulo, grupo ou código. Se o nome da tela (ou do caminho dela) bate, a tela entra inteira.
 * Ignora acento e caixa.
 */
export function searchActions(
  modules: PermissionModuleNode[],
  query: string
): SearchResultScreen[] {
  const term = normalize(query);
  if (!term) return [];

  return modules
    .flatMap(module => screensOf(module))
    .map(screen => {
      const all = [
        ...(screen.menuAction ? [screen.menuAction] : []),
        ...screen.readActions,
        ...screen.writeActions,
      ];
      const screenMatches = normalize(screenPathLabel(screen)).includes(term);
      const actions = screenMatches
        ? all
        : all.filter(action =>
            [action.label, action.hint, action.groupLabel, action.value].some(text =>
              normalize(text ?? '').includes(term)
            )
          );
      return { screen, actions };
    })
    .filter(result => result.actions.length > 0);
}
